import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Calendar as CalendarIcon } from 'lucide-react';
import { useState } from 'react';
import { format } from 'date-fns';
import { createComplaint, type CreateComplaintInput, type ComplaintStatus } from '@/core/api';
import { useAuth } from '@/core/AuthContext';
import { useFactory } from '@/core/hooks/useFactory';
import { useTenant } from '@/core/hooks/useTenant';
import { CustomerSelector, ArticleSelector, HierarchySelector } from '@/shared/components/selectors';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Textarea } from '@/shared/ui/textarea';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Calendar } from '@/shared/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover';
import { useToast } from '@/shared/hooks/use-toast';
import { cn } from '@/shared/lib/utils';

interface CreateComplaintDialogProps {
  trigger?: React.ReactNode;
  onCreated?: (complaintId: string) => void;
}

/**
 * Dialog for creating a new customer complaint.
 */
export const CreateComplaintDialog = ({ trigger, onCreated }: CreateComplaintDialogProps) => {
  const { user } = useAuth();
  const { selectedFactoryId } = useFactory();
  const { selectedTenantId } = useTenant();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);

  // Form state
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [complaintDate, setComplaintDate] = useState<Date>(new Date());
  const [productionDate, setProductionDate] = useState<Date | null>(null);
  const [status, setStatus] = useState<ComplaintStatus>('draft');
  const [complaintIdentifier, setComplaintIdentifier] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [comment, setComment] = useState('');
  
  // Hierarchy
  const [areaId, setAreaId] = useState<string | null>(null);
  const [lineId, setLineId] = useState<string | null>(null);
  const [stationId, setStationId] = useState<string | null>(null);
  
  // Article
  const [articleId, setArticleId] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: (input: CreateComplaintInput) => createComplaint(input),
    onSuccess: (data) => {
      toast({
        title: 'Complaint created',
        description: 'Customer complaint has been recorded successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['customer-complaints'] });
      queryClient.invalidateQueries({ queryKey: ['complaint-stats'] });
      resetForm();
      setOpen(false);
      // Open detail dialog for attachments
      if (onCreated && data?.id) {
        onCreated(data.id);
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create complaint',
        variant: 'destructive',
      });
    },
  });

  const resetForm = () => {
    setCustomerId(null);
    setComplaintDate(new Date());
    setProductionDate(null);
    setStatus('draft');
    setComplaintIdentifier('');
    setSerialNumber('');
    setComment('');
    setAreaId(null);
    setLineId(null);
    setStationId(null);
    setArticleId(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTenantId || !selectedFactoryId) {
      toast({
        title: 'Error',
        description: 'Please select a tenant and factory',
        variant: 'destructive',
      });
      return;
    }

    if (!customerId) {
      toast({
        title: 'Error',
        description: 'Please select a customer',
        variant: 'destructive',
      });
      return;
    }

    createMutation.mutate({
      tenant_id: selectedTenantId,
      factory_id: selectedFactoryId,
      customer_id: customerId,
      complaint_date: format(complaintDate, 'yyyy-MM-dd'),
      production_date: productionDate ? format(productionDate, 'yyyy-MM-dd') : null,
      status,
      complaint_identifier: complaintIdentifier || null,
      serial_number: serialNumber || null,
      comment: comment || null,
      area_id: areaId,
      line_id: lineId,
      station_id: stationId,
      article_id: articleId,
      created_by: user?.id,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New Complaint
          </Button>
        )}
      </DialogTrigger>

      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Record Customer Complaint</DialogTitle>
          <DialogDescription>
            Enter the details of the customer complaint. Fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Customer */}
          <div className="space-y-2">
            <Label>
              Customer <span className="text-destructive">*</span>
            </Label>
            <CustomerSelector value={customerId} onValueChange={setCustomerId} />
          </div>

          {/* Complaint Date */}
          <div className="space-y-2">
            <Label>
              Complaint Date <span className="text-destructive">*</span>
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn('w-full justify-start text-left font-normal')}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(complaintDate, 'PPP')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={complaintDate}
                  onSelect={(date) => date && setComplaintDate(date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={status} onValueChange={(val) => setStatus(val as ComplaintStatus)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="complaint_recorded">Complaint Recorded</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Article */}
          <div className="space-y-2">
            <Label>Article / Product (Optional)</Label>
            <ArticleSelector
              value={articleId}
              onValueChange={setArticleId}
              areaId={areaId}
              lineId={lineId}
              allowClear
            />
          </div>

          {/* Hierarchy */}
          <div className="space-y-2">
            <Label>Location (Optional)</Label>
            <HierarchySelector
              areaId={areaId}
              lineId={lineId}
              stationId={stationId}
              onAreaChange={setAreaId}
              onLineChange={setLineId}
              onStationChange={setStationId}
              includeStation
              allowClear
              showLabels={false}
            />
          </div>

          {/* Customer Identifier */}
          <div className="space-y-2">
            <Label htmlFor="complaint-identifier">Customer Complaint Identifier (Optional)</Label>
            <Input
              id="complaint-identifier"
              placeholder="e.g., BMW-2024-0123"
              value={complaintIdentifier}
              onChange={(e) => setComplaintIdentifier(e.target.value)}
            />
          </div>

          {/* Serial Number */}
          <div className="space-y-2">
            <Label htmlFor="serial-number">Serial Number (Optional)</Label>
            <Input
              id="serial-number"
              placeholder="e.g., SN123456789"
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
            />
          </div>

          {/* Production Date */}
          <div className="space-y-2">
            <Label>Production Date (Optional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn('w-full justify-start text-left font-normal')}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {productionDate ? format(productionDate, 'PPP') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={productionDate || undefined}
                  onSelect={(date) => setProductionDate(date || null)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Comment */}
          <div className="space-y-2">
            <Label htmlFor="comment">Comment (Optional)</Label>
            <Textarea
              id="comment"
              placeholder="Describe the complaint..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
            />
          </div>

          {/* Note about attachments */}
          <p className="text-sm text-muted-foreground">
            You can attach documents after creating the complaint.
          </p>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending || !customerId}>
              {createMutation.isPending ? 'Creating...' : 'Create Complaint'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
